/**
 * 客户管理页面组件 (crm-customers-page)
 *
 * 本文件是 CRM 系统的核心页面之一，负责客户列表的展示、搜索、筛选、
 * 新增/编辑/删除客户，以及客户相关的邮件往来、信息补全、跟进提醒等功能。
 *
 * 主要导出内容：
 * - `CrmCustomersPage`：Lit 组件类，注册为 `<crm-customers-page>` 自定义元素
 *
 * 与其他模块的关系：
 * - 通过 `customerStore` / `authStore` / `emailStore` 全局 store 管理状态
 * - 引用 `../api.js` 中的 customerApi / categoryApi / emailSendApi / emailApi / followUpApi
 * - 引用 `../components/` 下的多个子组件（table-toolbar、loading-overlay、select、pagination、category-modal 等）
 * - 引用 `../pages/customer-emails.js`（邮箱管理弹窗）、`../components/email-compose-panel.js`（邮件撰写面板）
 * - 引用 `../utils/countries.js` 获取国家列表、`../utils/email-helpers.js` 处理邮件辅助逻辑
 *
 * 关键设计决策：
 * - 客户表单弹窗内联在本组件中（而非独立组件），因为含邮箱管理表格逻辑过于复杂
 * - 弹窗内右侧使用 Tab 切换：往来邮件 / 跟进提醒 / 联系人 / 补充信息
 * - 邮件回复/转发/全部回复通过自定义事件在子组件与本组件间通信
 * - 使用 composeSession 递增计数器来触发邮件撰写面板重新初始化
 */
import { css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { authStore, customerStore, emailStore } from "../store.js";
import { BaseElement } from "../base-element.js";
import type { Customer, Email, CustomerContact, CustomerEmail } from "../types.js";
import { toast } from "../components/toast.js";
import { isNetworkError } from "../utils/index.js";
import { COUNTRIES } from "../utils/countries.js";
import { showConfirm } from "../components/confirm-dialog.js";
import { isSentByMe, removeSelfFromRecipients, buildForwardHeaderHtml, buildQuoteHeaderHtml, buildQuoteBodyHtml } from "../utils/email-helpers.js";
// 以下为组件内部依赖的子组件，import 后即可在模板中使用对应的自定义元素标签
import "../components/table-toolbar.js";
import "../components/loading-overlay.js";
import "../components/select.js";
import "../components/pagination.js";
import "../components/category-modal.js";
import {
  customerApi,
  categoryApi,
  emailSendApi,
  emailApi,
  followUpApi,
} from "../api.js";
import "../pages/customer-emails.js";
import "../components/email-compose-panel.js";
import "../components/side-panel.js";
import "../components/customer-email-panel.js";
import "../components/email-viewer.js";

/**
 * 客户管理页面组件
 *
 * 继承自 `BaseElement`，注册为 `<crm-customers-page>` 自定义元素。
 *
 * 职责：
 * - 渲染客户列表表格，支持按关键词搜索、按分类/国家/有邮件筛选
 * - 提供客户新增/编辑表单（内联弹窗），含多邮箱管理
 * - 展示客户详情面板，包含往来邮件时间线、跟进提醒、联系人、信息补全四个 Tab
 * - 集成邮件撰写面板，支持从客户列表直接发邮件、回复、全部回复、转发、快捷回复
 *
 * 状态管理：
 * - 通过订阅 `customerStore` 获取列表数据，通过订阅 `authStore` 获取用户角色（判断是否管理员）
 * - 邮件撰写相关的草稿状态在组件内独立管理
 */
@customElement("crm-customers-page")
export class CrmCustomersPage extends BaseElement {
  // ==================== 列表与分页状态 ====================
  /** 客户列表数据数组 */
  @state() private customers: Customer[] = [];
  /** 是否正在加载中（控制 loading 遮罩） */
  @state() private isLoading = false;
  /** 客户总数（用于分页计算） */
  @state() private total = 0;
  /** 当前页码（从 1 开始） */
  @state() private currentPage = 1;
  /** 每页条数 */
  @state() private perPage = 20;
  /** 搜索关键词输入框的实时值 */
  @state() private searchQuery = "";

  // ==================== 客户编辑弹窗状态 ====================
  /** 是否显示客户编辑/新增弹窗 */
  @state() private showPanel = false;
  /** 当前正在编辑的客户对象，为 null 表示新增模式 */
  @state() private editingCustomer: Customer | null = null;
  /** 表单字段校验错误信息，key 为字段名，value 为错误提示 */
  @state() private validationErrors: Record<string, string> = {};
  /** 弹窗中的邮箱列表（支持新增和编辑模式下的邮箱管理） */
  @state() private panelEmails: Array<{
    id: number | null;
    email: string;
    is_primary: number;
  }> = [];
  /** 新增邮箱输入框的值 */
  @state() private newPanelEmail = "";
  /** 新增邮箱时的错误提示 */
  @state() private panelEmailError = "";
  /** 当前正在编辑的邮箱行索引，null 表示无编辑中行 */
  @state() private panelEditingId: number | null = null;
  /** 编辑邮箱行时的输入值 */
  @state() private panelEditEmail = "";

  // ==================== 筛选与分类状态 ====================
  /** 客户分类列表（用于下拉筛选和表单选择） */
  @state() private categories: Array<{ id: number; name: string }> = [];
  /** 是否显示分类管理弹窗 */
  @state() private showCategoryModal = false;
  /** 列表筛选：分类 ID，0 表示全部分类 */
  @state() private categoryFilter = 0;
  /** 列表筛选：国家 ID，0 表示全部国家 */
  @state() private nationFilter = 0;
  /** 列表筛选：是否有往来邮件，1 表示仅显示有邮件的客户 */
  @state() private hasEmailFilter = 0;
  /** 列表筛选：创建日期（YYYY-MM-DD，空字符串表示不限），来自 URL ?created= 参数（日历跳转） */
  @state() private createdDateFilter = "";
  /** 表单中选择的分类 ID */
  @state() private formCategoryId = 0;
  /** 表单中选择的国家 ID */
  @state() private formNationId = 0;
  /** 国家列表，直接从 COUNTRIES 常量初始化 */
  @state() private countries: Array<{ id: number; name: string }> = COUNTRIES;

  // ==================== 邮件撰写状态 ====================
  /** 撰写邮件的收件人邮箱（兼容老字段，面板标题用） */
  @state() private composeToEmail = "";
  /** 撰写邮件关联的客户 ID */
  @state() private composeCustomerId = 0;
  /** 正在回复的原邮件对象，null 表示非回复模式 */
  @state() private replyingTo: Email | null = null;
  /** 回复邮件的主题（自动加 Re: / Fwd: 前缀） */
  @state() private replySubject = "";
  /** 撰写面板草稿：收件人列表 */
  @state() private composeDraftTo: string[] = [];
  /** 撰写面板草稿：抄送列表 */
  @state() private composeDraftCc: string[] = [];
  /** 撰写面板草稿：正文内容 */
  @state() private composeDraftContent = "";
  /** 撰写面板草稿：发件邮箱账号 ID */
  @state() private composeDraftAccountId = 0;
  /** 转发邮件的头部 HTML（包含原邮件的 From/To/Date/Subject 等信息） */
  @state() private composeForwardHeader = "";
  /** 转发邮件的原始 HTML 内容 */
  @state() private composeForwardHtml = "";
  /** 邮件撰写会话计数器，每次打开新的撰写/回复时递增，用于触发子组件重新初始化 */
  @state() private composeSession = 0;

  // ==================== 非响应式私有字段 ====================
  /** 当前用户拥有的邮箱地址列表（用于回复全部时过滤掉自己的邮箱） */
  private _selfEmails: string[] = [];
  /** 编辑客户时被删除的邮箱 ID 列表，提交时一并发送到后端删除 */
  private removedEmailIds: number[] = [];
  /** 记录鼠标按下是否发生在背景遮罩上（用于判断点击遮罩关闭弹窗） */
  private _backdropMousedown = false;

  // ==================== 详情面板 Tab 状态 ====================
  /** 详情面板当前激活的 Tab：timeline(往来邮件) / followup(跟进提醒) / contacts(联系人) / enrichment(补充信息) */
  @state() private panelTab: "timeline" | "followup" | "contacts" | "enrichment" = "timeline";
  /** 客户往来邮件时间线数据 */
  @state() private panelTimeline: Array<{ id: number; subject: string; email_date: number; folder: string }> = [];
  /** 客户跟进提醒列表 */
  @state() private panelFollowUps: Array<{ id: number; customer_id: number; remind_at: number; note: string; is_completed: number }> = [];
  /** 客户关联的联系人列表（信息补全发现的额外联系人） */
  @state() private panelContacts: CustomerContact[] = [];
  /** 客户邮箱列表（信息补全 Tab 展示，含验证状态） */
  @state() private panelEnrichmentEmails: CustomerEmail[] = [];
  /** 详情面板中正在查看的邮件对象（点击时间线邮件时设置，显示邮件查看器） */
  @state() private panelViewingEmail: Email | null = null;
  /** 当前用户是否为管理员（控制"信息补全"按钮的显示） */
  @state() private isAdmin = false;

  /** ESC 键处理函数：优先关闭分类弹窗，其次关闭客户编辑弹窗 */
  private handleEscKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      // 优先关闭浮在最上层的分类管理弹窗
      if (this.showCategoryModal) this.showCategoryModal = false;
      // 其次关闭客户编辑弹窗
      else if (this.showPanel) this.closePanel();
    }
  };

  /** 组件样式（当前为空，样式由 Tailwind CSS 类名提供） */
  static styles = css``;

  /** customerStore 订阅取消函数 */
  private _unsubscribe?: () => void;
  /** authStore 订阅取消函数 */
  private _unsubscribeAuth?: () => void;

  /**
   * 组件挂载到 DOM 时的生命周期回调
   *
   * 执行操作：
   * 1. 订阅 customerStore，将列表数据同步到组件状态
   * 2. 订阅 authStore，获取当前用户角色以判断是否管理员
   * 3. 加载客户列表和分类列表
   * 4. 注册自定义事件监听器（邮件回复/转发/快捷回复等）
   * 5. 注册全局 ESC 键监听（用于关闭弹窗）
   *
   * 副作用：发起网络请求（fetchCustomers、loadCategories、fetchAccounts）
   */
  connectedCallback() {
    super.connectedCallback();
    // 订阅客户 store，任何状态变更都会同步到本组件
    this._unsubscribe = customerStore.subscribe((state) => {
      this.customers = state.customers;
      this.isLoading = state.isLoading;
      this.total = state.total;
      this.currentPage = state.currentPage;
      this.perPage = state.perPage;
    });
    // 订阅认证 store，获取用户角色判断是否管理员
    this._unsubscribeAuth = authStore.subscribe((state) => {
      this.isAdmin = state.user?.role === 'admin';
    });
    // 解析 URL 的创建日期过滤参数（首页日历跳转携带 ?created=YYYY-MM-DD）
    const created = new URLSearchParams(window.location.search).get("created");
    if (created && /^\d{4}-\d{2}-\d{2}$/.test(created)) {
      this.createdDateFilter = created;
    }
    // 初始加载客户列表和分类列表
    this.loadCustomers();
    this.loadCategories();
    // 注册子组件冒泡上来的邮件相关自定义事件监听器
    this.addEventListener("crm-email-reply", this.onCustomerEmailReply);
    this.addEventListener("crm-email-reply-all", this.onCustomerEmailReplyAll);
    this.addEventListener("crm-email-forward", this.onCustomerEmailForward);
    this.addEventListener("crm-quick-reply", this.onCustomerQuickReply);
    // 注册全局键盘事件，用于 ESC 关闭弹窗
    document.addEventListener("keydown", this.handleEscKey);
  }

  /**
   * 组件从 DOM 移除时的生命周期回调
   *
   * 执行操作：
   * 1. 取消 customerStore / authStore 订阅，防止内存泄漏
   * 2. 移除全局键盘事件监听
   * 3. 移除自定义事件监听器
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    // 取消 store 订阅，避免组件销毁后仍触发状态更新
    this._unsubscribe?.();
    this._unsubscribeAuth?.();
    // 移除全局键盘事件
    document.removeEventListener("keydown", this.handleEscKey);
    // 移除自定义事件监听器
    this.removeEventListener("crm-email-reply", this.onCustomerEmailReply);
    this.removeEventListener("crm-email-reply-all", this.onCustomerEmailReplyAll);
    this.removeEventListener("crm-email-forward", this.onCustomerEmailForward);
    this.removeEventListener("crm-quick-reply", this.onCustomerQuickReply);
  }

  /**
   * 对单个客户启动信息补全任务
   *
   * 调用后端 enrichment 接口，异步抓取客户的邮箱和网站等信息。
   * 启动后延迟 3 秒自动刷新列表，以获取补全后的最新数据。
   *
   * @param customerId - 需要补全信息的客户 ID
   * @returns 无返回值，通过 toast 提示用户操作结果
   * @sideEffect 发起网络请求并可能触发列表刷新
   */
  private async enrichSingleCustomer(customerId: number) {
    try {
      const result = await customerApi.startEnrichment(['email', 'website'], [customerId]);
      toast.success(result.message || '信息补全已启动');
      // 延迟 3 秒后刷新列表，等待后端异步补全任务完成
      setTimeout(() => this.loadCustomers(), 3000);
    } catch (e: any) {
      toast.error(e?.message || '启动失败');
    }
  }

  /**
   * 加载客户列表数据
   *
   * 将当前的分页参数和筛选条件传递给 customerStore.fetchCustomers，
   * store 内部会发起 API 请求并通过订阅回调更新组件状态。
   *
   * @returns 无返回值，数据通过 store 订阅同步
   * @sideEffect 触发 customerStore 状态变更
   */
  private async loadCustomers() {
    // 创建日期过滤：把 YYYY-MM-DD 换算为覆盖当天（本地时区）的 Unix 秒区间
    let createdStart: number | undefined;
    let createdEnd: number | undefined;
    if (this.createdDateFilter) {
      const [y, m, d] = this.createdDateFilter.split("-").map(Number);
      createdStart = Math.floor(new Date(y, m - 1, d).getTime() / 1000);
      createdEnd = Math.floor(new Date(y, m - 1, d, 23, 59, 59).getTime() / 1000);
    }
    await customerStore.fetchCustomers({
      page: this.currentPage,
      per_page: this.perPage,
      // 关键词为空时不传该参数
      keyword: this.searchQuery || undefined,
      // 分类/国家/邮件筛选为 0 时表示不限，不传该参数
      category_id: this.categoryFilter || undefined,
      nation_id: this.nationFilter || undefined,
      has_email: this.hasEmailFilter || undefined,
      created_start: createdStart,
      created_end: createdEnd,
    });
  }

  /**
   * 搜索框输入事件处理器
   *
   * 实时更新 searchQuery 状态，但不立即触发搜索，
   * 等待用户按回车或点击搜索按钮时才执行查询。
   *
   * @param e - 输入事件对象
   */
  private handleSearch(e: Event) {
    this.searchQuery = (e.target as HTMLInputElement).value;
  }

  /**
   * 执行搜索
   *
   * 重置到第一页后加载客户列表。
   * 由搜索按钮点击或回车键触发。
   *
   * @returns 无返回值
   */
  private async doSearch() {
    this.currentPage = 1;
    await this.loadCustomers();
  }

  /**
   * 清除搜索条件并重新加载
   *
   * 清空搜索关键词输入框，并立即执行搜索（显示全部客户）。
   *
   * @returns 无返回值
   */
  private clearSearch() {
    this.searchQuery = "";
    this.doSearch();
  }

  /**
   * 打开邮件撰写面板
   *
   * 初始化邮件撰写相关的状态字段，递增 composeSession 以触发子组件重新初始化，
   * 然后调用 side-panel 的 show 方法弹出面板。
   *
   * @param email - 收件人邮箱地址
   * @param customerId - 关联的客户 ID
   * @sideEffect 请求邮箱账号列表（_ensureSelfEmails），操作 DOM 显示侧边面板
   */
  private openEmailCompose(email: string, customerId: number) {
    this.composeToEmail = email;
    this.composeCustomerId = customerId;
    this.replyingTo = null;
    this.replySubject = "";
    // 递增会话计数器，触发 email-compose-panel 组件重新初始化
    this.composeSession++;
    this.composeDraftTo = email ? [email] : [];
    this.composeDraftCc = [];
    this.composeDraftContent = "";
    this.composeDraftAccountId = 0;
    this.composeForwardHeader = "";
    this.composeForwardHtml = "";
    // 确保已加载当前用户的邮箱列表（用于回复全部时过滤自己）
    this._ensureSelfEmails();
    // 通过 shadow DOM 查询侧边面板元素并调用其 show 方法
    const panel = this.shadowRoot!.querySelector("#compose-panel") as any;
    panel?.show("发送邮件", 1080, "bottom");
  }

  /**
   * 关闭邮件撰写面板
   *
   * 清空所有邮件撰写相关的状态字段并关闭侧边面板。
   * 注意：不重置 composeSession，避免与 email-compose-panel 组件
   * 的 "composeSession > 0 才处理" 逻辑冲突。
   *
   * @sideEffect 操作 DOM 隐藏侧边面板
   */
  private closeEmailCompose() {
    this.composeToEmail = "";
    this.composeCustomerId = 0;
    this.replyingTo = null;
    this.replySubject = "";
    this.composeDraftTo = [];
    this.composeDraftCc = [];
    this.composeDraftContent = "";
    this.composeDraftAccountId = 0;
    this.composeForwardHeader = "";
    this.composeForwardHtml = "";
    // 注意：不重置 composeSession，避免与 email-compose 的"composeSession > 0 才处理"冲突
    const panel = this.shadowRoot!.querySelector("#compose-panel") as any;
    panel?.close();
  }

  /**
   * 从邮件撰写面板触发"创建客户"的事件处理器
   *
   * 当用户在撰写邮件时输入了一个不存在的邮箱地址，可以点击"创建客户"
   * 触发此事件。将邮箱填入 panelEmails 并打开新增客户弹窗。
   *
   * @param e - 自定义事件，detail 包含 email 字段
   * @sideEffect 设置 panelEmails 并打开客户编辑弹窗
   */
  private _onCreateCustomerFromCompose(e: CustomEvent) {
    const { email } = e.detail as { email: string };
    // 将邮箱预设为主邮箱（is_primary: 1）
    this.panelEmails = [{ id: null, email, is_primary: 1 }];
    this.openPanel();
  }

  /**
   * 分页页码变更处理器
   *
   * 更新当前页码并重新加载客户列表。
   *
   * @param page - 目标页码（从 1 开始）
   */
  private handlePageChange(page: number) {
    this.currentPage = page;
    this.loadCustomers();
  }

  /**
   * 确保已加载当前用户的邮箱地址列表
   *
   * 用于"回复全部"时过滤掉自己的邮箱地址，避免将自己加到收件人中。
   * 如果已经加载过（列表非空）则直接返回，否则从 emailStore 获取账号列表。
   *
   * @returns 无返回值，结果存储在 this._selfEmails 中
   * @sideEffect 可能触发 emailStore.fetchAccounts 网络请求
   */
  private async _ensureSelfEmails() {
    // 已加载过则跳过
    if (this._selfEmails.length > 0) return;
    try {
      // 确保邮箱账号列表已加载
      if (emailStore.state.accounts.length === 0) {
        await emailStore.fetchAccounts();
      }
      // 提取所有账号的邮箱地址
      this._selfEmails = emailStore.state.accounts
        .map((a: any) => a.address)
        .filter(Boolean);
    } catch {
      // 加载失败时设为空数组，不影响后续流程
      this._selfEmails = [];
    }
  }

  /**
   * 打开客户编辑/新增弹窗
   *
   * 初始化弹窗的所有状态字段。如果传入 customer 对象则为编辑模式，
   * 否则为新增模式。
   *
   * 编辑模式下会并行异步加载：
   * - 客户的邮箱列表（主邮箱排在前面）
   * - 往来邮件时间线（最近 10 封）
   * - 跟进提醒列表
   *
   * 注意：所有异步加载都有竞态检查（this.editingCustomer?.id !== cid），
   * 如果用户在加载过程中切换到其他客户，则丢弃旧结果。
   *
   * @param customer - 可选，传入则编辑该客户，不传则新增客户
   * @sideEffect 发起多个异步网络请求加载客户详情数据
   */
  private openPanel(customer?: Customer) {
    // 设置编辑模式或新增模式
    this.editingCustomer = customer || null;
    // 初始化表单中的分类和国家选择
    this.formCategoryId = customer?.category_id || 0;
    this.formNationId = customer?.nation_id || 0;
    // 初始化邮箱管理相关状态
    this.panelEmails = [];
    this.newPanelEmail = "";
    this.panelEmailError = "";
    this.panelEditingId = null;
    this.removedEmailIds = [];
    // 初始化详情面板 Tab 状态
    this.panelTab = "timeline";
    this.panelTimeline = [];
    this.panelFollowUps = [];
    this.panelContacts = [];
    this.panelEnrichmentEmails = [];
    this.panelViewingEmail = null;
    // 加载分类列表（弹窗中的下拉选项需要）
    this.loadCategories();
    this.showPanel = true;
    // 确保已加载用户邮箱列表
    this._ensureSelfEmails();

    // 编辑模式下，并行加载客户的邮箱列表、时间线、跟进提醒
    if (customer) {
      const cid = customer.id;
      // 加载客户邮箱列表，主邮箱排在前面
      customerApi.emails(cid).then((result) => {
        // 竞态检查：如果用户已切换到其他客户，丢弃旧结果
        if (this.editingCustomer?.id !== cid) return;
        const emails = (result as any).data || [];
        const primary = emails.filter((e: any) => e.is_primary === 1);
        const others = emails.filter((e: any) => e.is_primary !== 1);
        this.panelEmails = [
          ...primary.map((e: any) => ({ id: e.id as number, email: e.email, is_primary: 1 })),
          ...others.map((e: any) => ({ id: e.id as number, email: e.email, is_primary: 0 })),
        ];
      }).catch(() => {});
      // 加载往来邮件时间线（最近 10 封）
      customerApi.timeline(cid, 10).then((result) => {
        if (this.editingCustomer?.id !== cid) return;
        this.panelTimeline = (result as any).data || [];
      }).catch(() => {});
      // 加载跟进提醒列表
      followUpApi.list({ customer_id: cid }).then((result) => {
        if (this.editingCustomer?.id !== cid) return;
        this.panelFollowUps = (result as any).data || [];
      }).catch(() => {});
    }
  }

  /**
   * 关闭客户编辑/新增弹窗
   *
   * 重置弹窗相关的所有状态字段，包括编辑对象、校验错误、
   * Tab 状态、时间线、跟进提醒、联系人、补充信息等数据。
   *
   * @returns 无返回值
   * @sideEffect 重置多个状态字段
   */
  private closePanel() {
    this.showPanel = false;
    this.editingCustomer = null;
    this.validationErrors = {};
    this.panelEditingId = null;
    this.formNationId = 0;
    this.panelTab = "timeline";
    this.panelTimeline = [];
    this.panelFollowUps = [];
    this.panelContacts = [];
    this.panelEnrichmentEmails = [];
    this.panelViewingEmail = null;
  }

  /**
   * 查看时间线中的邮件详情
   *
   * 点击时间线邮件项时调用，通过 emailApi 获取完整邮件数据后
   * 设置到 panelViewingEmail，触发右侧面板切换到邮件查看器视图。
   *
   * @param emailId - 要查看的邮件 ID
   * @returns 无返回值，结果存储在 this.panelViewingEmail 中
   * @sideEffect 设置 panelViewingEmail，触发面板内容切换
   */
  private async viewTimelineEmail(emailId: number) {
    try {
      this.panelViewingEmail = await emailApi.get(emailId);
    } catch {
      /* 加载失败时忽略，panelViewingEmail 保持原值 */
    }
  }

  /**
   * 加载客户关联的联系人列表
   *
   * 联系人是指通过信息补全等功能发现的其他联系人。
   * 加载失败时清空联系人列表。
   *
   * @returns 无返回值，结果存储在 this.panelContacts 中
   * @sideEffect 发起网络请求获取联系人数据
   */
  private async loadContacts() {
    if (!this.editingCustomer) return;
    try {
      this.panelContacts = await customerApi.contacts(this.editingCustomer.id);
    } catch (e) {
      console.error('Failed to load contacts:', e);
      this.panelContacts = [];
    }
  }

  /**
   * 加载信息补全 Tab 所需的数据
   *
   * 加载客户的邮箱列表（含验证状态）和联系人列表。
   * 如果联系人列表已有数据则跳过加载，避免重复请求。
   *
   * @returns 无返回值，结果分别存储在 panelEnrichmentEmails 和 panelContacts 中
   * @sideEffect 可能发起两个网络请求
   */
  private async loadEnrichmentData() {
    if (!this.editingCustomer) return;
    try {
      // 加载邮箱列表（含验证状态信息）
      const result = await customerApi.emails(this.editingCustomer.id);
      this.panelEnrichmentEmails = (result as any).data || [];
    } catch (e) {
      console.error('Failed to load enrichment emails:', e);
      this.panelEnrichmentEmails = [];
    }
    // 如果联系人列表为空则加载联系人
    if (!this.panelContacts.length) {
      await this.loadContacts();
    }
  }

  /**
   * 打开邮箱管理弹窗
   *
   * 点击列表中的"邮箱"操作按钮时调用，通过 shadow DOM 查询
   * crm-customer-emails-modal 组件并调用其 show 方法。
   * 使用 requestAnimationFrame 确保组件已更新后再操作 DOM。
   *
   * @param customer - 包含 id 和 company_name 的客户对象
   * @sideEffect 操作 DOM 显示邮箱管理弹窗
   */
  private openEmailsModal(customer: { id: number; company_name: string }) {
    this.requestUpdate();
    // 等待 DOM 更新完成后操作
    requestAnimationFrame(() => {
      const modal = this.shadowRoot?.querySelector("#emailsModal") as any;
      if (modal) modal.show(customer.id, customer.company_name || "");
    });
  }

  /**
   * 邮件回复事件处理器
   *
   * 监听子组件冒泡的 `crm-email-reply` 自定义事件。
   * 设置回复相关的草稿状态：收件人为原邮件发件人，主题加 "Re:" 前缀，
   * 然后打开邮件撰写面板。
   *
   * @param e - 自定义事件，detail 包含原始 Email 对象
   * @sideEffect 设置多个草稿状态字段并打开侧边面板
   */
  private onCustomerEmailReply = (e: Event) => {
    const email = (e as CustomEvent).detail as Email;
    // 设置回复的原始邮件对象
    this.replyingTo = email;
    // 回复主题自动加 Re: 前缀
    this.replySubject = `Re: ${email.subject || ""}`;
    this.composeCustomerId = email.customer_id || 0;
    // 递增会话计数器触发子组件重新初始化
    this.composeSession++;
    // 回复自己发出的邮件时收件人指向原收件人列表，否则为原发件人
    this.composeDraftTo = isSentByMe(email)
      ? (email.send_to || []).map((a) => a.email).filter(Boolean)
      : email.send_from?.email
        ? [email.send_from.email]
        : [];
    this.composeDraftCc = [];
    this.composeDraftContent = "";
    // 使用原邮件的账号 ID 作为发件账号
    this.composeDraftAccountId = email.account_id || 0;
    this.composeForwardHeader = "";
    this.composeForwardHtml = "";
    // 保留兼容老字段（面板标题等）
    this.composeToEmail = email.send_from?.email || "";
    this.requestUpdate();
    // 在下一帧操作 DOM 显示面板
    requestAnimationFrame(() => {
      const panel = this.shadowRoot!.querySelector("#compose-panel") as any;
      panel?.show("回复邮件", 1080, "bottom");
    });
    // 异步获取原邮件完整正文，构建 Foxmail 风格的引用块
    void this._loadReplyQuote(email);
  };

  /**
   * 邮件"回复全部"事件处理器
   *
   * 监听子组件冒泡的 `crm-email-reply-all` 自定义事件。
   * 合并原邮件的发件人、收件人、抄送人作为新的收件人和抄送人，
   * 并过滤掉当前用户自己的邮箱地址（避免回复给自己）。
   * 使用 Set 去重后赋值给草稿状态。
   *
   * @param e - 自定义事件，detail 包含原始 Email 对象
   * @sideEffect 确保加载用户邮箱列表，设置多个草稿状态字段并打开侧边面板
   */
  private onCustomerEmailReplyAll = async (e: Event) => {
    const email = (e as CustomEvent).detail as Email;
    // 提取原邮件的发件人、收件人、抄送人邮箱
    const sender = email.send_from?.email || "";
    const toEmails = (email.send_to || []).map((a) => a.email).filter(Boolean);
    const ccEmails = (email.cc || []).map((a) => a.email).filter(Boolean);
    // 回复自己发出的邮件时不再把发件人（自己）加入收件人
    const mergedTo = [
      ...new Set(isSentByMe(email) ? toEmails : sender ? [sender, ...toEmails] : toEmails),
    ];
    // 抄送列表也使用 Set 去重
    const mergedCc = [...new Set(ccEmails)];

    // 确保已加载当前用户的邮箱列表
    await this._ensureSelfEmails();

    this.replyingTo = email;
    this.replySubject = `Re: ${email.subject || ""}`;
    this.composeCustomerId = email.customer_id || 0;
    this.composeSession++;
    // 过滤掉自己的邮箱地址，避免回复给自己
    this.composeDraftTo = removeSelfFromRecipients(mergedTo, this._selfEmails);
    this.composeDraftCc = removeSelfFromRecipients(mergedCc, this._selfEmails);
    this.composeDraftContent = "";
    this.composeDraftAccountId = email.account_id || 0;
    this.composeForwardHeader = "";
    this.composeForwardHtml = "";
    this.composeToEmail = "";
    this.requestUpdate();
    requestAnimationFrame(() => {
      const panel = this.shadowRoot!.querySelector("#compose-panel") as any;
      panel?.show("回复全部", 1080, "bottom");
    });
    // 异步获取原邮件完整正文，构建 Foxmail 风格的引用块
    void this._loadReplyQuote(email);
  };

  /**
   * 异步加载回复引用块内容
   *
   * 获取原邮件完整详情（含正文）后，把 Foxmail 风格的引用头
   * （"On 日期, 发件人 wrote:"）和原邮件正文设置到撰写面板，
   * 发送时会拼接在用户输入内容之后。获取失败时使用事件携带的
   * 列表级邮件对象兜底（正文可能为空）。
   *
   * @param email - 被回复的原始邮件对象（可能不含正文）
   */
  private async _loadReplyQuote(email: Email) {
    const composeSession = this.composeSession;
    let detail: Email = email;
    try {
      detail = await emailApi.get(email.id);
    } catch {
      // 获取失败时使用列表级数据兜底
    }
    if (composeSession !== this.composeSession) return;
    this.composeForwardHeader = buildQuoteHeaderHtml(detail);
    this.composeForwardHtml = buildQuoteBodyHtml(detail);
    this.requestUpdate();
  }

  /**
   * 邮件转发事件处理器
   *
   * 监听子组件冒泡的 `crm-email-forward` 自定义事件。
   * 设置转发相关的草稿状态（收件人为空，主题加 "Fwd:" 前缀），
   * 打开邮件撰写面板后异步获取完整邮件详情以构建转发头部和内容。
   *
   * @param e - 自定义事件，detail 包含原始 Email 对象
   * @sideEffect 打开侧边面板并异步获取邮件详情构建转发内容
   */
  private onCustomerEmailForward = async (e: Event) => {
    const email = (e as CustomEvent).detail as Email;
    // 转发模式下不设置 replyingTo
    this.replyingTo = null;
    // 转发主题自动加 Fwd: 前缀
    this.replySubject = `Fwd: ${email.subject || ""}`;
    this.composeCustomerId = email.customer_id || 0;
    this.composeSession++;
    const composeSession = this.composeSession;
    // 转发时收件人为空，需用户手动填写
    this.composeDraftTo = [];
    this.composeDraftCc = [];
    this.composeDraftContent = "";
    this.composeDraftAccountId = 0;
    this.composeForwardHeader = "";
    this.composeForwardHtml = "";
    this.composeToEmail = "";
    this.requestUpdate();
    requestAnimationFrame(() => {
      const panel = this.shadowRoot!.querySelector("#compose-panel") as any;
      panel?.show("转发邮件", 1080, "bottom");
    });

    // 异步获取完整邮件详情以构建转发头部和原始 HTML 内容
    try {
      const detail = await emailApi.get(email.id);
      if (composeSession !== this.composeSession) return;
      // 构建转发头部 HTML（包含原邮件的发件人、收件人、日期、主题等信息）
      this.composeForwardHeader = buildForwardHeaderHtml(detail);
      // 保留净化后的 HTML 正文，纯文本邮件转换换行并转义特殊字符
      this.composeForwardHtml = buildQuoteBodyHtml(detail);
    } catch {
      if (composeSession === this.composeSession) {
        // 获取失败时清空转发内容
        this.composeForwardHeader = "";
        this.composeForwardHtml = "";
      }
    }
  };

  /**
   * 快捷回复事件处理器
   *
   * 监听子组件冒泡的 `crm-quick-reply` 自定义事件。
   * 直接调用 emailSendApi.send 发送快捷回复邮件，不打开撰写面板。
   * 回复内容为纯文本（自动转为 HTML 段落），继承原邮件的 message_id
   * 和 references 以保持邮件线程关联。
   *
   * @param e - 自定义事件，detail 包含 email（原邮件）和 content（回复内容）
   * @sideEffect 发送邮件并通过 toast 提示结果
   */
  private onCustomerQuickReply = async (e: Event) => {
    const { email, content } = (e as CustomEvent).detail as {
      email: Email;
      content: string;
    };
    try {
      await emailSendApi.send({
        // 使用原邮件的账号 ID 作为发件账号
        account_id: email.account_id,
        // 收件人为原邮件发件人
        to_email: [email.send_from?.email || ""],
        // 回复主题加 Re: 前缀
        subject: `Re: ${email.subject || ""}`,
        // 将纯文本内容转为 HTML 段落（换行符转为 <br>）
        content: `<p>${content.replace(/\n/g, "<br>")}</p>`,
        content_type: "html",
        // 关联原邮件的 message_id，用于邮件线程匹配
        in_reply_to: email.message_id || "",
        // 将原邮件的 references 和 message_id 合并，保持完整的邮件线程引用链
        references: email.references
          ? `${email.references} ${email.message_id}`
          : email.message_id || "",
        // 线程 ID：优先使用原邮件的 thread_id，回退到 message_id
        thread_id: email.thread_id || email.message_id || "",
      });
      toast.info("快捷回复已发送");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "发送失败";
      // 网络错误不弹 toast（由全局拦截器处理）
      if (!isNetworkError(msg)) toast.error(msg);
    }
  };

  /**
   * 打开客户往来邮件历史面板
   *
   * 点击列表中"邮件"列的邮件图标时调用，打开右侧侧边面板显示
   * 该客户的所有往来邮件。同时操作两个 DOM 元素：
   * - historyContent（crm-customer-email-panel）：加载并显示邮件内容
   * - historyPanel（crm-side-panel）：显示面板容器
   *
   * @param customer - 客户对象，包含 id 和名称
   * @sideEffect 操作 DOM 显示往来邮件侧边面板
   */
  private openEmailHistory(customer: Customer) {
    this.requestUpdate();
    requestAnimationFrame(() => {
      const panel = this.shadowRoot?.querySelector("#historyPanel") as any;
      const content = this.shadowRoot?.querySelector("#historyContent") as any;
      if (panel && content) {
        // 先加载邮件内容，传入客户 ID 和显示名称
        content.show(
          customer.id,
          customer.company_name || customer.name || "客户",
        );
        // 再显示面板容器，宽度 960px，从右侧滑出
        panel.show("往来邮件", 960, "right");
      }
    });
  }

  /**
   * 根据分类 ID 获取分类名称
   *
   * 在列表和表单中展示分类名称时调用。
   *
   * @param categoryId - 分类 ID
   * @returns 分类名称字符串，未找到或 ID 为 0 时返回 "-"
   */
  private getCategoryName(categoryId: number): string {
    if (!categoryId || categoryId === 0) return "-";
    const cat = this.categories.find((c) => c.id === categoryId);
    return cat ? cat.name : "-";
  }

  // ========== 邮箱管理 ==========

  /**
   * 添加邮箱到面板邮箱列表
   *
   * 验证邮箱格式（标准 email 正则）和重复性后，将新邮箱追加到列表。
   * 新增的邮箱 id 为 null（表示尚未持久化到后端），is_primary 为 0（非主邮箱）。
   *
   * 验证规则：
   * - 邮箱不能为空
   * - 必须符合标准邮箱格式
   * - 不能与已有邮箱重复（不区分大小写）
   *
   * @sideEffect 更新 panelEmails 和 newPanelEmail 状态
   */
  private addPanelEmail() {
    const trimmed = this.newPanelEmail.trim();
    if (!trimmed) return;
    // 标准邮箱格式验证
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      this.panelEmailError = "邮箱格式不正确";
      return;
    }
    // 重复性检查（不区分大小写）
    if (
      this.panelEmails.some(
        (e) => e.email.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      this.panelEmailError = "该邮箱已存在";
      return;
    }
    this.panelEmailError = "";
    // 追加新邮箱，id 为 null 表示尚未持久化
    this.panelEmails = [
      ...this.panelEmails,
      { id: null, email: trimmed, is_primary: 0 },
    ];
    // 清空输入框
    this.newPanelEmail = "";
  }

  /**
   * 邮箱输入框的键盘事件处理器
   *
   * 按回车键时阻止默认行为并调用 addPanelEmail 添加邮箱。
   *
   * @param e - 键盘事件对象
   */
  private handleEmailInputKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      this.addPanelEmail();
    }
  }

  /**
   * 确认删除邮箱
   *
   * 弹出确认对话框，用户确认后从列表中移除该邮箱。
   * 主邮箱不可删除。已持久化的邮箱（id 不为 null）记录到 removedEmailIds，
   * 在表单提交时一并发送到后端删除。
   *
   * @param index - 要删除的邮箱在 panelEmails 中的索引
   * @sideEffect 更新 panelEmails 和 removedEmailIds 状态
   */
  private async deletePanelEmailConfirm(index: number) {
    const email = this.panelEmails[index];
    // 主邮箱不可删除
    if (email.is_primary) return;
    // 弹出确认对话框
    if (!(await showConfirm(`确定删除邮箱 ${email.email} 吗？`))) return;
    // 已持久化的邮箱记录 ID，提交时删除
    if (email.id) this.removedEmailIds.push(email.id);
    // 从列表中移除
    this.panelEmails = this.panelEmails.filter((_, i) => i !== index);
  }

  /**
   * 进入邮箱行编辑模式
   *
   * 记录当前编辑的行索引并初始化编辑输入框的值为当前邮箱地址。
   *
   * @param index - 要编辑的邮箱行索引
   * @sideEffect 设置 panelEditingId 和 panelEditEmail 状态
   */
  private startPanelEdit(index: number) {
    this.panelEditingId = index;
    this.panelEditEmail = this.panelEmails[index].email;
  }

  /**
   * 取消邮箱行编辑模式
   *
   * 将 panelEditingId 重置为 null，退出编辑模式。
   */
  private cancelPanelEdit() {
    this.panelEditingId = null;
  }

  /**
   * 保存邮箱行编辑
   *
   * 验证编辑后的邮箱格式和重复性，通过后更新列表中对应行的邮箱地址。
   *
   * 验证规则：
   * - 不能为空且必须符合标准邮箱格式
   * - 不能与其他行重复（不区分大小写，排除自身）
   *
   * @param index - 正在编辑的邮箱行索引
   * @sideEffect 更新 panelEmails 并退出编辑模式
   */
  private savePanelEdit(index: number) {
    const trimmed = this.panelEditEmail.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("邮箱格式不正确");
      return;
    }
    // 排除自身后检查重复
    if (
      this.panelEmails.some(
        (e, i) =>
          i !== index && e.email.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      toast.error("该邮箱已存在");
      return;
    }
    // 更新对应行的邮箱地址
    this.panelEmails = this.panelEmails.map((e, i) =>
      i === index ? { ...e, email: trimmed } : e,
    );
    this.panelEditingId = null;
  }

  /**
   * 设置主邮箱
   *
   * 将指定索引的邮箱设为主邮箱，同时取消其他邮箱的主邮箱标记。
   * 一个客户只能有一个主邮箱。
   *
   * @param index - 要设为主邮箱的行索引
   * @sideEffect 更新 panelEmails 中所有行的 is_primary 字段
   */
  private setPanelPrimary(index: number) {
    this.panelEmails = this.panelEmails.map((e, i) => ({
      ...e,
      // 仅目标行设为 1，其余设为 0
      is_primary: i === index ? 1 : 0,
    }));
  }

  /**
   * 加载客户分类列表
   *
   * 调用 categoryApi.list 获取全部分类，映射为 { id, name } 数组。
   * 用于列表筛选下拉和表单中的分类选择。
   * 加载失败时静默忽略（不影响主要功能）。
   *
   * @returns 无返回值，结果存储在 this.categories 中
   * @sideEffect 发起网络请求获取分类数据
   */
  private async loadCategories() {
    try {
      const cats = await categoryApi.list();
      this.categories = (cats as any[]).map((c: any) => ({
        id: c.id,
        name: c.name,
      }));
    } catch {
      /* 加载失败时静默忽略，保持空分类列表 */
    }
  }

  /**
   * 客户表单提交处理器
   *
   * 收集表单数据，验证必填字段（联系人姓名、邮箱列表、主邮箱），
   * 验证通过后根据编辑/新增模式分别调用 updateCustomer/createCustomer，
   * 然后同步邮箱列表（删除/新增/设主邮箱），最后关闭弹窗并刷新列表。
   *
   * 验证规则：
   * - 联系人姓名不能为空
   * - 至少需要一个邮箱地址
   * - 必须设置一个主邮箱
   *
   * @param e - 表单提交事件对象
   * @sideEffect 发起网络请求创建/更新客户及邮箱，关闭弹窗，刷新列表
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const errors: Record<string, string> = {};
    // 提取并验证联系人姓名
    const name = (formData.get("name") as string).trim();
    // 检查是否有主邮箱
    const hasPrimary = this.panelEmails.some((e) => e.is_primary === 1);
    if (!name) errors.name = "联系人不能为空";
    if (this.panelEmails.length === 0) errors.email = "至少需要一个邮箱地址";
    else if (!hasPrimary) errors.email = "请设置一个主邮箱";
    // 存在校验错误时直接返回，不提交
    if (Object.keys(errors).length > 0) {
      this.validationErrors = errors;
      return;
    }
    this.validationErrors = {};

    // 构建提交数据对象
    const data = {
      company_name: (formData.get("company_name") as string).trim(),
      name,
      job_title: (formData.get("job_title") as string).trim(),
      phone: (formData.get("phone") as string).trim(),
      address: (formData.get("address") as string).trim(),
      remark: (formData.get("remark") as string).trim(),
      nation_id: this.formNationId,
      website: (formData.get("website") as string).trim(),
      category_id: this.formCategoryId,
    };

    try {
      if (this.editingCustomer) {
        // 编辑模式：更新客户信息并同步邮箱
        const customer = await customerStore.updateCustomer(
          this.editingCustomer.id,
          data,
        );
        await this.syncCustomerEmails(customer.id);
      } else {
        // 新增模式：创建客户并添加额外邮箱
        const customer = await customerStore.createCustomer(data);
        await this.addExtraEmails(customer.id);
      }
      this.closePanel();
      this.loadCustomers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "保存失败";
      // 网络错误不弹 toast（由全局拦截器处理）
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 同步编辑模式下客户邮箱的变更
   *
   * 按顺序处理三类邮箱操作：
   * 1. 删除：遍历 removedEmailIds，逐个调用 API 删除已标记删除的邮箱
   * 2. 新增：遍历 panelEmails 中 id 为 null 的邮箱，逐个调用 API 添加
   * 3. 设主：找到 is_primary 为 1 且已有 id 的邮箱，调用 API 设为主邮箱
   *
   * 注意：每个操作都独立 try-catch，单个失败不影响其他操作。
   *
   * @param customerId - 客户 ID
   * @sideEffect 发起多个网络请求同步邮箱数据
   */
  private async syncCustomerEmails(customerId: number) {
    // 1. 删除已标记删除的邮箱
    for (const id of this.removedEmailIds) {
      try {
        await customerApi.deleteEmail(customerId, id);
      } catch {
        /* 单个删除失败不影响其他操作 */
      }
    }
    // 2. 新增未持久化的邮箱（id 为 null）
    for (const email of this.panelEmails) {
      if (email.id === null) {
        try {
          await customerApi.addEmail(customerId, email.email);
        } catch {
          /* 单个添加失败不影响其他操作 */
        }
      }
    }
    // 3. 设置主邮箱（仅当主邮箱已有 id 时）
    const primary = this.panelEmails.find((e) => e.is_primary === 1);
    if (primary && primary.id) {
      try {
        await customerApi.setPrimaryEmail(customerId, primary.id);
      } catch {
        /* 设置主邮箱失败时忽略 */
      }
    }
  }

  /**
   * 新增模式下添加额外邮箱
   *
   * 创建新客户后，将所有 panelEmails 中的邮箱逐个添加到后端。
   * 然后重新获取邮箱列表以找到新添加邮箱的 ID，设置主邮箱。
   *
   * 注意：新建客户时第一个邮箱可能已通过创建 API 自动添加，
   * 此处会再次尝试添加所有邮箱（重复添加会被后端忽略或报错，但不影响流程）。
   *
   * @param customerId - 新创建的客户 ID
   * @sideEffect 发起多个网络请求添加邮箱并设置主邮箱
   */
  private async addExtraEmails(customerId: number) {
    // 逐个添加邮箱
    for (const email of this.panelEmails) {
      try {
        await customerApi.addEmail(customerId, email.email);
      } catch {
        /* 单个添加失败不影响其他操作 */
      }
    }
    // 设置主邮箱：需先获取邮箱列表找到对应的 ID
    if (this.panelEmails.length > 0) {
      const primary = this.panelEmails.find((e) => e.is_primary === 1);
      if (primary) {
        try {
          // 重新获取邮箱列表，匹配主邮箱地址以获取其 ID
          const result = await customerApi.emails(customerId);
          const emails = (result as any).data || [];
          const found = emails.find(
            (e: any) => e.email.toLowerCase() === primary.email.toLowerCase(),
          );
          if (found) {
            await customerApi.setPrimaryEmail(customerId, found.id);
          }
        } catch {
          /* 设置主邮箱失败时忽略 */
        }
      }
    }
  }

  /**
   * 删除客户
   *
   * 弹出确认对话框，用户确认后调用 customerStore.deleteCustomer。
   * 删除失败时通过 toast 提示错误信息。
   *
   * @param customer - 要删除的客户对象
   * @sideEffect 弹出确认对话框，可能触发删除网络请求
   */
  private async handleDelete(customer: Customer) {
    if (!(await showConfirm(`确定要删除客户 "${customer.company_name}" 吗？`)))
      return;
    try {
      await customerStore.deleteCustomer(customer.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "删除失败";
      // 网络错误不弹 toast（由全局拦截器处理）
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 渲染信息补全 Tab 内容
   *
   * 展示客户的信息补全结果，包括：
   * - 补全状态徽章（已完成/待处理/补全中/失败）
   * - 公司信息（全称、行业、规模、Logo）
   * - 公司简介
   * - 社交链接
   * - 邮箱验证状态（有效/格式正确/无效/风险/待验证）
   * - 发现的额外联系人
   *
   * 如果当前没有编辑的客户则返回空模板。
   *
   * @returns Lit html 模板
   */
  private renderEnrichmentTab() {
    const c = this.editingCustomer;
    if (!c) return html``;

    // 补全状态映射表：后端状态值 -> { 标签, CSS 类名 }
    const statusMap: Record<string, { label: string; cls: string }> = {
      completed: { label: '已完成', cls: 'bg-success/10 text-success' },
      pending: { label: '待处理', cls: 'bg-bg text-text-muted' },
      enriching: { label: '补全中', cls: 'bg-info/10 text-info' },
      failed: { label: '失败', cls: 'bg-error/10 text-error' },
    };
    // 获取当前客户的补全状态，默认为 pending
    const st = statusMap[c.enrichment_status] || statusMap['pending'];

    // 邮箱验证状态映射表：后端状态值 -> { 标签, CSS 类名 }
    const vStatusMap: Record<string, { label: string; cls: string }> = {
      valid: { label: '有效', cls: 'bg-success/10 text-success' },
      valid_format: { label: '格式正确', cls: 'bg-info/10 text-info' },
      invalid: { label: '无效', cls: 'bg-error/10 text-error' },
      risky: { label: '风险', cls: 'bg-warning/10 text-warning' },
      pending: { label: '待验证', cls: 'bg-bg text-text-muted' },
    };

    // 解析社交链接 JSON 字符串（可能为 null 或无效 JSON）
    let socialLinks: string[] = [];
    try { socialLinks = c.social_links ? JSON.parse(c.social_links) : []; } catch {
      // 历史数据可能不是合法 JSON，此时按无社交链接展示。
    }

    return html`
      <div class="space-y-4">
        <!-- 补全状态行：状态徽章 + 更新时间 + 失败原因 -->
        <div class="flex items-center gap-3">
          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}">${st.label}</span>
          ${c.enriched_at ? html`<span class="text-xs text-text-muted">更新于 ${new Date(c.enriched_at * 1000).toLocaleString("zh-CN")}</span>` : ''}
          ${c.enrichment_note ? html`<span class="text-xs text-error">${c.enrichment_note}</span>` : ''}
        </div>

        <!-- 公司信息卡片：Logo + 全称 + 行业 + 规模 -->
        ${(c.company_full_name || c.industry || c.company_size || c.logo_url) ? html`
        <div class="border border-border rounded-md p-3 space-y-2">
          <div class="flex items-start gap-3">
            ${c.logo_url ? html`<img src=${c.logo_url} class="w-10 h-10 rounded object-contain flex-shrink-0 bg-white" alt="logo" @error=${(e: Event) => { (e.target as HTMLImageElement).style.display = 'none'; }} />` : ''}
            <div class="space-y-1 flex-1">
              <div class="text-xs font-medium text-text-muted uppercase tracking-wide">公司信息</div>
              ${c.company_full_name ? html`<div class="text-sm font-medium text-text">${c.company_full_name}</div>` : ''}
              ${c.industry ? html`<div class="text-sm"><span class="text-text-muted">行业：</span><span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">${c.industry}</span></div>` : ''}
              ${c.company_size ? html`<div class="text-sm"><span class="text-text-muted">规模：</span><span class="text-text">${c.company_size} 人</span></div>` : ''}
            </div>
          </div>
        </div>` : ''}

        <!-- 公司简介卡片 -->
        ${c.company_description ? html`
        <div class="border border-border rounded-md p-3 space-y-2">
          <div class="text-xs font-medium text-text-muted uppercase tracking-wide">公司简介</div>
          <p class="text-sm text-text leading-relaxed m-0">${c.company_description}</p>
        </div>` : ''}

        <!-- 社交链接列表 -->
        ${socialLinks.length ? html`
        <div class="border border-border rounded-md p-3 space-y-2">
          <div class="text-xs font-medium text-text-muted uppercase tracking-wide">社交链接</div>
          ${socialLinks.map(link => html`
            <div class="text-sm truncate">
              <a href=${link} target="_blank" rel="noopener" class="text-primary hover:underline">${link}</a>
            </div>
          `)}
        </div>` : ''}

        <!-- 邮箱验证状态列表 -->
        <div class="border border-border rounded-md p-3 space-y-2">
          <div class="text-xs font-medium text-text-muted uppercase tracking-wide">邮箱验证</div>
          ${this.panelEnrichmentEmails.length === 0
            ? html`<div class="text-sm text-text-muted py-2">暂无邮箱数据</div>`
            : this.panelEnrichmentEmails.map(e => {
                // 获取当前邮箱的验证状态，默认为 pending
                const vs = vStatusMap[e.validation_status] || vStatusMap['pending'];
                return html`
                  <div class="flex items-center gap-2 text-sm py-1">
                    <span class="flex-1 truncate">${e.email}</span>
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${vs.cls}">${vs.label}</span>
                    ${e.validation_detail ? html`<span class="text-xs text-text-muted max-w-[120px] truncate" title=${e.validation_detail}>${e.validation_detail}</span>` : ''}
                  </div>
                `;
              })}
        </div>

        <!-- 信息补全发现的额外联系人列表 -->
        ${this.panelContacts.length ? html`
        <div class="border border-border rounded-md p-3 space-y-2">
          <div class="text-xs font-medium text-text-muted uppercase tracking-wide">发现的联系人</div>
          ${this.panelContacts.map(ct => html`
            <div class="text-sm py-1 flex items-center gap-2">
              <span class="font-medium text-text">${ct.name || '未知'}</span>
              ${ct.email ? html`<span class="text-text-secondary text-xs">${ct.email}</span>` : ''}
              ${ct.phone ? html`<span class="text-text-secondary text-xs">${ct.phone}</span>` : ''}
              <span class="ml-auto text-xs text-text-muted">${ct.source}</span>
            </div>
          `)}
        </div>` : ''}
      </div>
    `;
  }

  /**
   * 组件主渲染方法
   *
   * 渲染客户管理页面的完整 UI，包含以下主要区块：
   *
   * 1. 顶部工具栏区
   *    - 页面标题"客户管理"
   *    - 搜索框（支持关键词搜索，回车触发）
   *    - 分类筛选下拉
   *    - 国家筛选下拉（支持搜索）
   *    - 搜索按钮
   *    - "有往来邮件"筛选切换按钮
   *    - "管理分类"按钮
   *    - "新增客户"按钮
   *    - 表格工具栏组件（含刷新按钮和总数显示）
   *
   * 2. 客户列表表格区
   *    - 表头：ID / 公司名称 / 联系人 / 邮箱 / 分类 / 电话 / 网站 / 邮件 / 操作
   *    - 表体：遍历 customers 数组渲染每行数据
   *    - 加载中遮罩
   *    - 分页组件
   *
   * 3. 客户编辑/新增弹窗（showPanel 为 true 时显示）
   *    - 左侧 40%：编辑表单（分类、公司名称、联系人、职位、邮箱表格、电话、地址、国家、网站、备注）
   *    - 右侧 60%：Tab 面板（往来邮件 / 跟进提醒 / 联系人 / 补充信息）或邮件查看器
   *
   * 4. 分类管理弹窗
   * 5. 邮箱管理弹窗
   * 6. 邮件撰写侧边面板
   * 7. 往来邮件历史侧边面板
   *
   * @returns Lit html 模板
   */
  render() {
    return html`
      <!-- 根容器：纵向弹性布局占满父容器高度 -->
      <div class="flex flex-col h-full">
        <!-- ==================== 顶部工具栏区 ==================== -->
        <div class="flex items-center gap-3 mb-2 flex-shrink-0 flex-wrap">
          <!-- 页面标题 -->
          <span class="text-base font-semibold text-text">客户管理</span>
          <!-- 搜索框：支持回车搜索，带清除按钮 -->
          <div
            class="w-96 h-[32px] flex items-center rounded border border-border bg-surface focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-4 h-4 ml-3 shrink-0 text-text-muted"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <!-- 搜索输入框：回车触发搜索，input 事件更新 searchQuery -->
            <input
              type="text"
              placeholder="搜索ID、公司或邮箱..."
              .value=${this.searchQuery}
              @input=${this.handleSearch}
              @keydown=${(e: KeyboardEvent) =>
                e.key === "Enter" && this.doSearch()}
              class="flex-1 h-full px-2 text-sm bg-transparent border-none outline-none placeholder:text-text-muted"
            />
            <!-- 搜索关键词不为空时显示清除按钮 -->
            ${this.searchQuery
              ? html`
                  <button
                    class="shrink-0 mr-1 w-5 h-5 flex items-center justify-center rounded-full text-text-muted hover:text-text hover:bg-border transition-colors cursor-pointer"
                    @click=${this.clearSearch}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      class="w-3.5 h-3.5"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                `
              : ""}
          </div>
          <!-- 分类筛选下拉 -->
          <div style="width: 140px;">
            <crm-select
              fullWidth
              .value=${String(this.categoryFilter)}
              .options=${[
                { label: "全部分类", value: "0" },
                ...this.categories.map((c) => ({
                  label: c.name,
                  value: String(c.id),
                })),
              ]}
              @change=${(e: CustomEvent) => {
                this.categoryFilter = parseInt(e.detail.value);
                this.currentPage = 1;
                this.loadCustomers();
              }}
            ></crm-select>
          </div>
          <!-- 国家筛选下拉（支持搜索） -->
          <div style="width: 160px;">
            <crm-select
              fullWidth
              searchable
              .value=${String(this.nationFilter)}
              .options=${[
                { label: "全部国家", value: "0" },
                ...this.countries.map((c) => ({
                  label: c.name,
                  value: String(c.id),
                })),
              ]}
              @change=${(e: CustomEvent) => {
                this.nationFilter = parseInt(e.detail.value);
                this.currentPage = 1;
                this.loadCustomers();
              }}
            ></crm-select>
          </div>
          <!-- 搜索按钮：触发 doSearch -->
          <button
            class="bg-primary hover:bg-primary-hover text-white px-3 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer shadow-sm shadow-primary/20"
            @click=${this.doSearch}
          >
            搜索
          </button>
          <!-- "有往来邮件"筛选切换按钮：点击切换 hasEmailFilter -->
          <button
            class="px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer border ${this
              .hasEmailFilter
              ? "border-[#ff6b6b] bg-[#ffe3e3] text-[#c92a2a]"
              : "border-[#ff8787] bg-[#fff5f5] text-[#e03131] hover:bg-[#ffe3e3]"}"
            @click=${() => {
              this.hasEmailFilter = this.hasEmailFilter ? 0 : 1;
              this.currentPage = 1;
              this.loadCustomers();
            }}
          >
            有往来邮件${this.hasEmailFilter ? " ✓" : ""}
          </button>
          <!-- 创建日期过滤徽章：由首页日历跳转携带，点击 ✕ 清除过滤 -->
          ${this.createdDateFilter
            ? html`
                <span
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-success/40 bg-success/10 text-success"
                >
                  创建日期：${this.createdDateFilter}
                  <button
                    class="p-0 bg-transparent border-0 cursor-pointer text-success hover:opacity-70 leading-none"
                    title="清除创建日期过滤"
                    @click=${() => {
                      this.createdDateFilter = "";
                      this.currentPage = 1;
                      this.loadCustomers();
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                  </button>
                </span>
              `
            : ""}
          <!-- 管理分类按钮：打开分类管理弹窗 -->
          <button
            class="px-3 py-1.5 text-sm font-medium text-white bg-info hover:bg-info/90 rounded transition-colors cursor-pointer"
            @click=${() => {
              this.showCategoryModal = true;
            }}
          >
            管理分类
          </button>
          <!-- 弹性占位，将右侧按钮推到最右 -->
          <div class="flex-1"></div>
          <!-- 新增客户按钮：打开新增弹窗 -->
          <button
            class="bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer shadow-sm shadow-primary/20 flex items-center gap-2"
            @click=${() => this.openPanel()}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-4 h-4"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" /></svg
            >新增客户
          </button>
          <!-- 表格工具栏组件：显示总数和刷新按钮 -->
          <crm-table-toolbar
            .total=${this.total}
            .label=${"个客户"}
            .loading=${this.isLoading}
            @crm-toolbar-refresh=${() => this.loadCustomers()}
          ></crm-table-toolbar>
        </div>

        <!-- ==================== 客户列表表格区 ==================== -->
        <div class="flex-1 min-h-0">
          <!-- 表格卡片容器：圆角 + 阴影 + 弹性布局 -->
          <div
            class="bg-surface rounded-md shadow-lg flex flex-col h-full overflow-hidden relative"
          >
            <!-- 加载中遮罩组件 -->
            <crm-loading-overlay
              .active=${this.isLoading}
            ></crm-loading-overlay>
            <!-- 表格滚动容器 -->
            <div class="overflow-auto flex-1 min-h-0">
              <table class="w-full">
                <!-- 表头：ID / 公司名称 / 联系人 / 邮箱 / 分类 / 电话 / 网站 / 邮件 / 操作 -->
                <thead>
                  <tr class="border-b border-border">
                    <!-- ID 列 -->
                    <th
                      class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                    >
                      ID
                    </th>
                    <!-- 公司名称列 -->
                    <th
                      class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                    >
                      公司名称
                    </th>
                    <th
                      class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                    >
                      联系人
                    </th>
                    <th
                      class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                    >
                      邮箱
                    </th>
                    <th
                      class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                    >
                      分类
                    </th>
                    <th
                      class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                    >
                      电话
                    </th>
                    <th
                      class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                    >
                      网站
                    </th>
                    <th
                      class="px-4 py-2.5 text-center text-xs font-medium text-text-secondary uppercase tracking-wider"
                      style="width:60px;"
                    >
                      邮件
                    </th>
                    <th
                      class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider whitespace-nowrap"
                    >
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- 空数据状态：非加载中且列表为空时显示 -->
                  ${!this.isLoading && this.customers.length === 0
                    ? html`<tr>
                        <td
                          colspan="9"
                          class="text-center py-12 text-text-muted"
                        >
                          暂无客户数据
                        </td>
                      </tr>`
                    : this.customers.map(
                        (customer) =>
                          /* 每个客户一行：hover 高亮 + 边框分隔 */
                          html` <tr
                            class="hover:bg-bg/50 transition-colors border-b border-border"
                          >
                            <!-- 客户 ID -->
                            <td
                              class="px-4 py-2 text-sm text-text font-semibold"
                            >
                              ${customer.id}
                            </td>
                            <!-- 公司名称（含补全状态指示点：绿色=已完成，红色=失败） -->
                            <td
                              class="px-4 py-2 text-sm font-medium text-text max-w-[300px] truncate"
                              title=${customer.company_name || ""}
                            >
                              <span>${customer.company_name || "-"}</span>${customer.enrichment_status === 'completed' ? html`
                                <span class="inline-block w-1.5 h-1.5 rounded-full bg-success ml-1" title="信息已补全"></span>
                              ` : customer.enrichment_status === 'failed' ? html`
                                <span class="inline-block w-1.5 h-1.5 rounded-full bg-error ml-1" title="补全失败"></span>
                              ` : ''}
                            </td>
                            <!-- 联系人名称 -->
                            <td
                              class="px-4 py-2 text-sm text-text max-w-[150px] truncate"
                              title=${customer.name || ""}
                            >
                              ${customer.name || "-"}
                            </td>
                            <!-- 邮箱：点击直接打开撰写面板 -->
                            <td
                              class="px-4 py-2 text-sm font-mono max-w-[200px] truncate"
                              title=${customer.email || ""}
                            >
                              ${customer.email
                                ? html`<a
                                    class="text-info cursor-pointer hover:underline"
                                    @click=${() =>
                                      this.openEmailCompose(
                                        customer.email!,
                                        customer.id,
                                      )}
                                    >${customer.email}</a
                                  >`
                                : html`<span class="text-text-muted">-</span>`}
                            </td>
                            <!-- 分类名称 -->
                            <td class="px-4 py-2 text-sm text-text">
                              ${this.getCategoryName(customer.category_id)}
                            </td>
                            <!-- 电话 -->
                            <td
                              class="px-4 py-2 text-sm text-text max-w-[200px] truncate"
                              title=${customer.phone || ""}
                            >
                              ${customer.phone || "-"}
                            </td>
                            <!-- 网站（点击在新标签页打开） -->
                            <td
                              class="px-4 py-2 text-sm text-text max-w-[200px] truncate"
                            >
                              ${customer.website
                                ? html`<a
                                    href=${customer.website}
                                    target="_blank"
                                    rel="noopener"
                                    class="text-info hover:underline"
                                    >${customer.website}</a
                                  >`
                                : html`<span class="text-text-muted">-</span>`}
                            </td>
                            <!-- 邮件列：有往来邮件时显示邮件图标按钮 -->
                            <td class="px-4 py-2 text-sm text-center">
                              ${customer.email_count > 0
                                ? html` <button
                                    class="text-info hover:text-info/80 transition-colors cursor-pointer"
                                    title="共 ${customer.email_count} 封往来邮件"
                                    @click=${() =>
                                      this.openEmailHistory(customer)}
                                  >
                                    <svg
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      stroke-width="2"
                                      class="w-4 h-4 mx-auto"
                                    >
                                      <path
                                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                                      />
                                      <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                  </button>`
                                : html`<span class="text-text-muted">-</span>`}
                            </td>
                            <!-- 操作列：邮箱管理 / 详情 / 删除 -->
                            <td class="px-4 py-2 whitespace-nowrap">
                              <!-- 邮箱管理按钮：打开邮箱管理弹窗 -->
                              <button
                                class="text-text-secondary hover:text-text text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                @click=${() => this.openEmailsModal(customer)}
                              >
                                邮箱
                              </button>
                              <!-- 详情按钮：打开客户编辑弹窗 -->
                              <button
                                class="text-primary hover:text-primary-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                @click=${() => this.openPanel(customer)}
                              >
                                详情
                              </button>
                              <!-- 删除按钮：弹出确认后删除 -->
                              <button
                                class="text-error hover:text-error-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                @click=${() => this.handleDelete(customer)}
                              >
                                删除
                              </button>
                            </td>
                          </tr>`,
                      )}
                </tbody>
              </table>
            </div>
            <!-- 分页组件：page-change 事件触发 handlePageChange -->
            <crm-pagination
              .current=${this.currentPage}
              .perPage=${this.perPage}
              .total=${this.total}
              @page-change=${(e: CustomEvent) => this.handlePageChange(e.detail.page)}
            ></crm-pagination>
          </div>
        </div>

        <!-- ==================== 客户编辑/新增弹窗 ==================== -->
        <!-- 弹窗内联在组件中（因含邮箱管理表格逻辑过于复杂，不宜抽为独立组件） -->
        ${this.showPanel
          ? html` <div
              class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50"
              @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }}
              @click=${() => { if (this._backdropMousedown) this.closePanel(); }}
            >
              <!-- 弹窗内容容器：阻止事件冒泡防止误关闭 -->
              <div
                class="bg-surface rounded-md shadow-xl w-full mx-4 border border-border overflow-y-auto"
                style="width:100%;max-height:90vh;"
                @click=${(e: Event) => e.stopPropagation()}
              >
                <!-- 弹窗头部：sticky 固定在顶部，含标题和关闭按钮 -->
                <div
                  class="sticky top-0 bg-surface flex justify-between items-center px-6 pt-5 pb-0 border-b border-border"
                >
                  <div class="flex items-center gap-6">
                    <!-- 标题：编辑模式显示公司名或"编辑客户"，新增模式显示"新增客户" -->
                    <h2 class="m-0 font-semibold text-xl text-text">
                      ${this.editingCustomer
                        ? this.editingCustomer.company_name || "编辑客户"
                        : "新增客户"}
                    </h2>
                  </div>
                  <!-- 关闭按钮 -->
                  <button
                    class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors"
                    @click=${this.closePanel}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      class="w-5 h-5"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                </div>
                <!-- 弹窗主体：左右分栏布局（左侧编辑表单 40%，右侧 Tab 面板 60%） -->
                <div class="flex" style="min-height:60vh;">
                  <!-- 左侧 40%：编辑表单区 -->
                  <div class="p-6 overflow-y-auto border-r border-border" style="width:40%;">
                    ${this.editingCustomer
                      ? html`
                          <!-- 编辑模式信息栏：显示客户 ID、创建时间、更新时间 -->
                          <div class="text-xs text-text-muted mb-4 flex gap-4 items-center">
                            <span>ID: ${this.editingCustomer.id}</span>
                            <span>创建: ${new Date(this.editingCustomer.created_at * 1000).toLocaleString("zh-CN")}</span>
                            <span>更新: ${new Date(this.editingCustomer.updated_at * 1000).toLocaleString("zh-CN")}</span>
                            ${this.isAdmin ? html`
                              <!-- 管理员可见：信息补全按钮 -->
                              <span class="flex-1"></span>
                              <button type="button"
                                class="px-2 py-0.5 text-xs font-medium rounded border border-warning text-warning hover:bg-warning/10 transition-colors cursor-pointer"
                                @click=${() => this.enrichSingleCustomer(this.editingCustomer!.id)}
                              >信息补全</button>
                            ` : ''}
                          </div>
                                  `
                                : ""}
                              <!-- 客户编辑表单：提交事件绑定 handleSubmit -->
                              <form
                                @submit=${this.handleSubmit}
                                class="space-y-4"
                              >
                    <!-- 客户分类选择：支持搜索，旁边有"添加分类"按钮 -->
                    <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >客户分类</label
                      >
                      <div class="flex gap-2 items-start">
                        <div class="flex-1">
                          <crm-select
                            searchable
                            .value=${String(this.formCategoryId)}
                            .options=${[
                              { label: "未分类", value: "0" },
                              ...this.categories.map((c) => ({
                                label: c.name,
                                value: String(c.id),
                              })),
                            ]}
                            fullWidth
                            @change=${(e: CustomEvent) => {
                              this.formCategoryId = parseInt(e.detail.value);
                            }}
                          ></crm-select>
                        </div>
                        <!-- 添加分类快捷按钮：打开分类管理弹窗 -->
                        <button
                          type="button"
                          class="px-3 py-1.5 text-sm font-medium text-primary border border-primary/30 rounded hover:bg-primary/5 transition-colors cursor-pointer flex-shrink-0"
                          @click=${() => {
                            this.showCategoryModal = true;
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <!-- 公司名称输入框 -->
                    <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >公司名称</label
                      ><input
                        type="text"
                        name="company_name"
                        .value=${this.editingCustomer?.company_name || ""}
                        class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                      />
                    </div>
                    <!-- 联系人姓名（必填，带校验错误提示） -->
                    <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >联系人 <span class="text-error">*</span></label
                      ><input
                        type="text"
                        name="name"
                        .value=${this.editingCustomer?.name || ""}
                        class="w-full px-3.5 py-1.5 border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${this
                          .validationErrors.name
                          ? "border-error"
                          : "border-border"}"
                      />${this.validationErrors.name
                        ? html`<p class="mt-1 text-xs text-error">
                            ${this.validationErrors.name}
                          </p>`
                        : ""}
                    </div>
                    <!-- 职位输入框 -->
                    <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >职位</label
                      ><input
                        type="text"
                        name="job_title"
                        .value=${this.editingCustomer?.job_title || ""}
                        class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                      />
                    </div>
                    <!-- 邮箱地址管理（必填）：支持添加多个邮箱并设置主邮箱 -->
                    <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >邮箱地址 <span class="text-error">*</span></label
                      >
                      ${this.validationErrors.email
                        ? html`<p class="mt-0 mb-2 text-xs text-error">
                            ${this.validationErrors.email}
                          </p>`
                        : ""}
                      <!-- 添加邮箱输入框 + 添加按钮 -->
                      <div class="flex gap-2 mb-2">
                        <input
                          type="email"
                          placeholder="输入邮箱地址..."
                          .value=${this.newPanelEmail}
                          @input=${(e: Event) => {
                            this.newPanelEmail = (
                              e.target as HTMLInputElement
                            ).value;
                            this.panelEmailError = "";
                          }}
                          @keydown=${this.handleEmailInputKeydown}
                          class="flex-1 px-3.5 py-1.5 border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${this
                            .panelEmailError
                            ? "border-error"
                            : "border-border"}"
                        />
                        <button
                          type="button"
                          class="bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer"
                          @click=${this.addPanelEmail}
                        >
                          添加
                        </button>
                      </div>
                      ${this.panelEmailError
                        ? html`<p class="mt-0 mb-2 text-xs text-error">
                            ${this.panelEmailError}
                          </p>`
                        : ""}
                      ${this.panelEmails.length > 0
                        ? html` <div
                            class="border border-border rounded-md overflow-hidden"
                          >
                            <!-- 邮箱列表表格 -->
                            <table class="w-full">
                              <thead>
                                <tr class="bg-bg/50 border-b border-border">
                                  <th
                                    class="px-3 py-2 text-left text-xs font-medium text-text-muted"
                                  >
                                    邮箱地址
                                  </th>
                                  <th
                                    class="px-3 py-2 text-center text-xs font-medium text-text-muted w-16"
                                  >
                                    主邮箱
                                  </th>
                                  <th
                                    class="px-3 py-2 text-left text-xs font-medium text-text-muted w-40"
                                  >
                                    操作
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                ${this.panelEmails.map(
                                  (email, i) =>
                                    html` <tr
                                      class="border-b border-border last:border-b-0"
                                    >
                                      ${this.panelEditingId === i
                                        ? html`
                                            <!-- 编辑模式行：输入框 + 保存 + 取消按钮 -->
                                            <td colspan="3" class="px-3 py-2">
                                              <div
                                                class="flex gap-2 items-center"
                                              >
                                                <input
                                                  type="email"
                                                  class="flex-1 px-3 py-1.5 border border-border rounded text-sm bg-surface focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                                                  .value=${this.panelEditEmail}
                                                  @input=${(e: Event) =>
                                                    (this.panelEditEmail = (
                                                      e.target as HTMLInputElement
                                                    ).value)}
                                                  @keydown=${(
                                                    e: KeyboardEvent,
                                                  ) => {
                                                    if (e.key === "Enter")
                                                      this.savePanelEdit(i);
                                                  }}
                                                />
                                                <button
                                                  type="button"
                                                  class="bg-success hover:bg-success-hover text-white px-3 py-1.5 rounded text-xs font-medium transition-colors cursor-pointer"
                                                  @click=${() =>
                                                    this.savePanelEdit(i)}
                                                >
                                                  保存
                                                </button>
                                                <button
                                                  type="button"
                                                  class="bg-bg hover:bg-border border border-border text-text-secondary px-3 py-1.5 rounded text-xs font-medium transition-colors cursor-pointer"
                                                  @click=${this.cancelPanelEdit}
                                                >
                                                  取消
                                                </button>
                                              </div>
                                            </td>
                                          `
                                        : html`
                                            <!-- 显示模式行：邮箱地址 + 主邮箱标记 + 操作按钮 -->
                                            <td
                                              class="px-3 py-2 text-sm text-text"
                                            >
                                              ${email.email}
                                            </td>
                                            <td class="px-3 py-2 text-center">
                                              ${email.is_primary
                                                ? html`<svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="3"
                                                    class="w-5 h-5 text-success mx-auto"
                                                  >
                                                    <path d="M20 6 9 17l-5-5" />
                                                  </svg>`
                                                : html`<span
                                                    class="text-text-muted text-sm"
                                                    >-</span
                                                  >`}
                                            </td>
                                            <td class="px-3 py-2">
                                              <div
                                                class="flex gap-1.5 flex-wrap"
                                              >
                                                <!-- 编辑邮箱按钮 -->
                                                <button
                                                  type="button"
                                                  class="text-primary hover:text-primary-hover text-xs font-medium px-2 py-1.5 rounded transition-colors cursor-pointer"
                                                  @click=${() =>
                                                    this.startPanelEdit(i)}
                                                >
                                                  编辑
                                                </button>
                                                ${!email.is_primary
                                                  ? html`<button
                                                      type="button"
                                                      class="text-info hover:opacity-80 text-xs font-medium px-2 py-1.5 rounded transition-colors cursor-pointer"
                                                      @click=${() =>
                                                        this.setPanelPrimary(i)}
                                                    >
                                                      设为主
                                                    </button>`
                                                  : ""}
                                                ${!email.is_primary
                                                  ? html`<button
                                                      type="button"
                                                      class="text-error hover:text-error-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                                      @click=${() =>
                                                        this.deletePanelEmailConfirm(
                                                          i,
                                                        )}
                                                    >
                                                      删除
                                                    </button>`
                                                  : ""}
                                              </div>
                                            </td>
                                          `}
                                    </tr>`,
                                )}
                              </tbody>
                            </table>
                          </div>`
                        : html`<p
                            class="text-xs text-text-muted text-center py-3 border border-dashed border-border rounded-md"
                          >
                            暂无邮箱地址
                          </p>`}
                    </div>
                    <!-- 电话输入框 -->
                    <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >电话</label
                      ><input
                        type="text"
                        name="phone"
                        .value=${this.editingCustomer?.phone || ""}
                        class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                      />
                    </div>
                    <!-- 地址输入框 -->
                    <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >地址</label
                      ><input
                        type="text"
                        name="address"
                        .value=${this.editingCustomer?.address || ""}
                        class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                      />
                    </div>
                     <!-- 国家选择（支持搜索的下拉） -->
                     <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >国家</label
                      ><crm-select
                        fullWidth
                        searchable
                        .options=${[
                          { label: "请选择", value: "0" },
                          ...this.countries.map((c) => ({
                            label: c.name,
                            value: String(c.id),
                          })),
                        ]}
                        .value=${String(this.formNationId)}
                        @change=${(e: CustomEvent) => {
                          this.formNationId = parseInt(e.detail.value) || 0;
                        }}
                      ></crm-select>
                    </div>
                    <!-- 网站输入框 -->
                    <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >网站</label
                      ><input
                        type="text"
                        name="website"
                        .value=${this.editingCustomer?.website || ""}
                        class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                      />
                    </div>
                    <!-- 备注文本域 -->
                    <div>
                      <label class="block mb-1.5 text-sm font-medium text-text"
                        >备注</label
                      ><textarea
                        name="remark"
                        rows="3"
                        class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                      >
${this.editingCustomer?.remark || ""}</textarea
                      >
                    </div>
                    <!-- 表单底部操作按钮：取消 + 保存 -->
                    <div
                      class="flex justify-end gap-2 mt-6 pt-4 border-t border-border"
                    >
                      <!-- 取消按钮 -->
                      <button
                        type="button"
                        class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer"
                        @click=${this.closePanel}
                      >
                        取消
                      </button>
                      <!-- 保存按钮（type=submit 触发表单提交） -->
                      <button
                        type="submit"
                        class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20"
                      >
                        保存
                      </button>
                    </div>
                  </form>
                    </div>
                    <!-- ==================== 右侧 60%：Tab 面板或邮件查看器 ==================== -->
                    <div class="flex flex-col" style="width:60%;">
                      ${this.panelViewingEmail
                        ? html`
                          <!-- 邮件查看器模式：点击时间线邮件后显示，含返回按钮 -->
                          <div class="flex items-center gap-3 px-4 pt-3 pb-3 border-b border-border flex-shrink-0">
                            <!-- 返回按钮：清除 panelViewingEmail 回到 Tab 列表 -->
                            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors flex-shrink-0"
                                    @click=${() => { this.panelViewingEmail = null; }}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="m15 18-6-6 6-6"/></svg>
                            </button>
                          </div>
                          <div class="flex-1 overflow-y-auto">
                            <!-- 邮件查看器组件：渲染邮件 HTML 内容 -->
                            <email-viewer
                              .email=${this.panelViewingEmail}
                              .isSentByMe=${isSentByMe(this.panelViewingEmail)}
                              .hideBorder=${true}
                            ></email-viewer>
                          </div>
                        `
                        : html`
                          <!-- Tab 导航栏：四个 Tab 按钮带数量标识 -->
                          <div class="flex gap-1 px-4 pt-4 pb-2 border-b border-border flex-shrink-0">
                            <!-- Tab 1: 往来邮件（显示数量） -->
                            <button
                              class="px-3 py-1 text-sm rounded-full transition-colors cursor-pointer ${this.panelTab === 'timeline' ? 'bg-black text-white' : 'bg-white text-text border border-border'}"
                              @click=${() => { this.panelTab = 'timeline'; }}
                            >往来邮件 (${this.panelTimeline.length})</button>
                            <!-- Tab 2: 跟进提醒（显示数量） -->
                            <button
                              class="px-3 py-1 text-sm rounded-full transition-colors cursor-pointer ${this.panelTab === 'followup' ? 'bg-black text-white' : 'bg-white text-text border border-border'}"
                              @click=${() => { this.panelTab = 'followup'; }}
                            >跟进提醒 (${this.panelFollowUps.length})</button>
                            <!-- Tab 3: 联系人（显示数量，首次点击时加载） -->
                            <button
                              class="px-3 py-1 text-sm rounded-full transition-colors cursor-pointer ${this.panelTab === 'contacts' ? 'bg-black text-white' : 'bg-white text-text border border-border'}"
                              @click=${() => { this.panelTab = 'contacts'; if (!this.panelContacts.length) this.loadContacts(); }}
                            >联系人 (${this.panelContacts.length})</button>
                            <!-- Tab 4: 补充信息（首次点击时加载） -->
                            <button
                              class="px-3 py-1 text-sm rounded-full transition-colors cursor-pointer ${this.panelTab === 'enrichment' ? 'bg-black text-white' : 'bg-white text-text border border-border'}"
                              @click=${() => { this.panelTab = 'enrichment'; if (!this.panelEnrichmentEmails.length) this.loadEnrichmentData(); }}
                            >补充信息</button>
                          </div>
                          <div class="overflow-y-auto flex-1 p-4">
                            <!-- Tab: 往来邮件时间线列表 -->
                            ${this.panelTab === 'timeline' ? html`
                              ${this.panelTimeline.length === 0
                                ? html`<div class="text-sm text-text-muted py-8 text-center">暂无往来邮件</div>`
                                : this.panelTimeline.map(
                                    (t) => html`
                                      <!-- 时间线邮件项：点击查看邮件详情 -->
                                      <div class="flex items-center gap-3 text-sm py-2.5 border-b border-border last:border-0 cursor-pointer hover:bg-bg rounded px-2 transition-colors -mx-2"
                                           @click=${() => this.viewTimelineEmail(t.id)}>
                                        <!-- 邮件日期 -->
                                        <span class="text-text-muted w-24 flex-shrink-0 text-xs">${new Date(t.email_date * 1000).toLocaleString("zh-CN")}</span>
                                        <!-- 邮件方向指示点：蓝色=已发送/发件箱，绿色=收件 -->
                                        <span class="w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.folder === 'sent' || t.folder === 'outbox' ? 'bg-info' : 'bg-success'}"></span>
                                        <!-- 邮件主题 -->
                                        <span class="text-text truncate flex-1 font-medium">${t.subject || "无主题"}</span>
                                        <!-- 右箭头图标 -->
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 text-text-muted flex-shrink-0"><path d="m9 18 6-6-6-6"/></svg>
                                      </div>
                                    `,
                                  )}
                            ` : this.panelTab === 'followup' ? html`
                              <!-- Tab: 跟进提醒列表 -->
                              ${this.panelFollowUps.length === 0
                                ? html`<div class="text-sm text-text-muted py-8 text-center">暂无跟进提醒</div>`
                                : this.panelFollowUps.map(
                                    (f) => html`
                                      <!-- 跟进提醒项 -->
                                      <div class="flex items-center gap-3 text-sm py-2.5 border-b border-border last:border-0 px-2">
                                        <!-- 提醒时间 -->
                                        <span class="text-text-muted w-28 flex-shrink-0 text-xs">${new Date(f.remind_at * 1000).toLocaleString("zh-CN")}</span>
                                        <!-- 完成状态徽章：绿色=已完成，黄色=待跟进 -->
                                        ${f.is_completed
                                          ? html`<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">已完成</span>`
                                          : html`<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning">待跟进</span>`}
                                        <!-- 跟进备注 -->
                                        <span class="text-text truncate flex-1">${f.note || "-"}</span>
                                      </div>
                                    `,
                                  )}
                            ` : this.panelTab === 'contacts' ? html`
                              <!-- Tab: 联系人列表 -->
                              ${this.panelContacts.length === 0
                                ? html`<div class="text-sm text-text-muted py-8 text-center">暂无联系人</div>`
                                : this.panelContacts.map(
                                    (c) => html`
                                      <!-- 联系人项：姓名 + 职位 + 来源标签 + 邮箱/电话 -->
                                      <div class="text-sm py-2.5 border-b border-border last:border-0 px-2">
                                        <div class="flex items-center gap-2">
                                          <span class="font-medium text-text">${c.name || '未知'}</span>
                                          ${c.job_title ? html`<span class="text-text-muted text-xs">${c.job_title}</span>` : ''}
                                          <span class="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-bg text-text-muted">${c.source}</span>
                                        </div>
                                        <div class="flex items-center gap-3 mt-1 text-xs text-text-secondary">
                                          ${c.email ? html`<span>${c.email}</span>` : ''}
                                          ${c.phone ? html`<span>${c.phone}</span>` : ''}
                                        </div>
                                      </div>
                                    `,
                                  )}
                            ` : html`
                              <!-- Tab: 补充信息（调用 renderEnrichmentTab） -->
                              ${this.renderEnrichmentTab()}
                            `}
                          </div>
                        `}
                    </div>
                  </div>
              </div>
            </div>`
          : ""}

        <!-- ==================== 分类管理弹窗 ==================== -->
        <!-- 分类变更后重新加载分类列表并关闭弹窗 -->
        <crm-category-modal
          .open=${this.showCategoryModal}
          .categories=${this.categories}
          @category-changed=${() => {
            this.loadCategories();
            this.showCategoryModal = false;
          }}
        ></crm-category-modal>

        <!-- ==================== 邮箱管理弹窗 ==================== -->
        <crm-customer-emails-modal id="emailsModal"></crm-customer-emails-modal>

        <!-- ==================== 邮件撰写侧边面板 ==================== -->
        <!-- 当有收件人或回复邮件或会话活跃时渲染邮件撰写组件 -->
        <crm-side-panel
          id="compose-panel"
          @crm-panel-close=${this.closeEmailCompose}
        >
          ${this.composeToEmail || this.replyingTo || this.composeSession > 0
            ? html`
                <!-- 邮件撰写面板：传递草稿状态、回复/转发相关数据 -->
                <crm-email-compose-panel
                  .toEmail=${this.composeToEmail}
                  .customerId=${this.composeCustomerId}
                  .subject=${this.replySubject}
                  .composeSession=${this.composeSession}
                  .draftTo=${this.composeDraftTo}
                  .draftCc=${this.composeDraftCc}
                  .draftContent=${this.composeDraftContent}
                  .draftAccountId=${this.composeDraftAccountId}
                  .forwardHeader=${this.composeForwardHeader}
                  .forwardHtml=${this.composeForwardHtml}
                  .replyToMessageId=${this.replyingTo?.message_id || ""}
                  .replyReferences=${this.replyingTo?.references
                    ? `${this.replyingTo.references} ${this.replyingTo.message_id || ""}`
                    : this.replyingTo?.message_id || ""}
                  .replyThreadId=${this.replyingTo?.thread_id ||
                  this.replyingTo?.message_id ||
                  ""}
                  @crm-email-compose-sent=${this.closeEmailCompose}
                  @crm-email-compose-close=${this.closeEmailCompose}
                  @crm-create-customer=${this._onCreateCustomerFromCompose}
                >
                </crm-email-compose-panel>
              `
            : ""}
        </crm-side-panel>

        <!-- ==================== 往来邮件历史侧边面板 ==================== -->
        <!-- z-index 设置为 30，低于撰写面板以便撰写面板可覆盖 -->
        <crm-side-panel id="historyPanel" style="--crm-panel-z: 30;">
          <crm-customer-email-panel
            id="historyContent"
          ></crm-customer-email-panel>
        </crm-side-panel>
      </div>
    `;
  }
}
